import React, { useState, useRef, useEffect, ReactNode } from 'react'

import {
  supportedLanguages,
  calculateTotal,
  CountryConfiguration,
  formatPriceText,
  PurchaseGetDraftResponse,
} from '@common'
import cx from 'classnames'
import { AnimatePresence, LazyMotion, m, HTMLMotionProps } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'

import { AnimatePresenceIfEnabled } from '@/components/Animations/AnimatePresenceIfEnabled'
import { useHideBodyScroll } from '@/utils/useHideBodyScroll'

import { useGetDraftQuery, useUpdateDraftMutationState } from '../../api'
import { Button } from '../../components/buttons/Button'
import { IconButton } from '../../components/buttons/IconButton'
import { ArrowRight, Cross, ShoppingBag, ShoppingBagMedium, ShoppingBagThin } from '../../components/Icons'
import { compareCartRows } from '../../utils/compareCartRows'
import { gtmBeginCheckout } from '../../utils/gtm'
import { useAppDispatch } from '../../utils/useAppDispatch'
import { useAppSelector } from '../../utils/useAppSelector'
import { useBreakpoints } from '../../utils/useBreakpoints'
import useOnClickOutside from '../../utils/useOnClickOutside'
import { EligibilityCopy } from '../cart/EligibilityCopy'
import { ProductRowList } from '../cart/ProductRow'
import { CheckoutRow } from '../checkout/checkoutSlice'
import { useIsLoggedIn } from '../common/useIsLoggedIn'
import { useLocale } from '../locale/useLocale'
import { clearNotification } from '../notifications/notificationSlice'
import { setOverlayView } from '../overlay/overlaySlice'

import styles from './CartButton.module.scss'

type CartDropdownProps = React.PropsWithChildren<HTMLMotionProps<'div'>> & { disableAnimations?: boolean }
type DivProps = React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>

const CartDropdown = React.forwardRef<HTMLDivElement, CartDropdownProps>(
  ({ children, disableAnimations, ...props }, ref) => {
    if (disableAnimations) {
      return (
        <div ref={ref} {...(props as unknown as DivProps)}>
          {children}
        </div>
      )
    }

    return (
      <m.div ref={ref} {...props}>
        {children}
      </m.div>
    )
  },
)

CartDropdown.displayName = 'CartDropdown'

const CartHeader: React.FC<{ subtitle?: string | JSX.Element | ReactNode; onClose: () => void }> = ({
  subtitle,
  onClose,
}) => {
  return (
    <div className={styles.Header}>
      <div>
        <ShoppingBagMedium width={28} height={28} />
        <p>{subtitle}</p>
      </div>
      <IconButton className={styles.CloseButton} onClick={onClose} data-cy="cart-button-close">
        <Cross />
      </IconButton>
    </div>
  )
}

const resolveCartTotal = (
  rows: CheckoutRow[],
  market: CountryConfiguration | undefined,
  draft: PurchaseGetDraftResponse | undefined,
) => {
  if (!market) {
    return 0
  }
  if (draft) {
    return draft.checkout.total
  }
  return calculateTotal(
    rows.map(({ product: { id: sku, prices }, quantity }) => ({ sku, prices, quantity })),
    market.currencyCountry,
  ).total.toNumber()
}

const Cart = React.forwardRef<HTMLDivElement, { onClose: () => void; disableAnimations: boolean }>(function Cart(
  { disableAnimations, onClose },
  ref,
) {
  const dispatch = useAppDispatch()
  const isLoggedIn = useIsLoggedIn()
  const { push } = useRouter()
  const t = useTranslations()
  const locale = useAppSelector((state) => state.config.locale)
  const { isLoading, isError } = useUpdateDraftMutationState()
  const { data: draft } = useGetDraftQuery(undefined, { pollingInterval: 10000, skip: !isLoggedIn })
  const rows = useAppSelector((state) => state.checkout.rows)
  const itemCount = rows.reduce((acc, row) => acc + row.quantity, 0)
  const isEmpty = rows.length === 0
  const inEligibleRow = rows.find((row) => !row.eligibility?.isEligible)
  const paymentDisabled = isEmpty || inEligibleRow !== undefined || isLoading || isError
  const market = useAppSelector((selector) => selector.config.market ?? undefined)
  const taxType = market?.taxType
  const currency = market?.currency
  const total = resolveCartTotal(rows, market, draft)
  const isRTL = supportedLanguages[locale].rightToLeft

  const gotoCheckout = () => {
    push('/checkout')
    onClose()
    gtmBeginCheckout(rows, market?.countryCode)
  }

  return (
    <>
      <CartDropdown
        className={styles.CartDropdown}
        ref={ref}
        data-cy="cart-dropdown"
        initial={{ x: isRTL ? -313 : 313 }}
        animate={{ x: 0 }}
        exit={{ x: isRTL ? -313 : 313 }}
        transition={{ duration: 0.2 }}
        disableAnimations={disableAnimations}
      >
        <CartHeader subtitle={t.rich('cart_items', { count: itemCount })} onClose={onClose} />
        <AnimatePresence>
          {inEligibleRow && <EligibilityCopy row={inEligibleRow} className={styles.CartEligibility} />}
        </AnimatePresence>
        {isEmpty ? (
          <div className={styles.CartEmpty}>
            <ShoppingBagThin />
            <p>{t('summary_no_products')}</p>
          </div>
        ) : (
          <div className={styles.CartList} data-cy="cart-list">
            <ProductRowList products={rows} className={styles.ProductRow} draft={draft} />
          </div>
        )}
        <div className={cx(styles.Actions, { [styles.Hidden]: isEmpty })}>
          <div>
            <p className={styles.CartTotalTitle}>
              {taxType === 'SALES_TAX' ? t('checkout_cart_sub') : t('summary_total')}
            </p>
            <p data-cy="cart-total" className={styles.CartTotalAmount}>
              {total ? formatPriceText(total, locale, currency) : '-'}
            </p>
          </div>
          {isLoggedIn ? (
            <Button
              disabled={paymentDisabled}
              additionalClasses={styles.CheckoutButton}
              loading={isLoading}
              data-cy="cart-button-checkout"
              onClick={gotoCheckout}
            >
              <ArrowRight />
              {t('checkout_checkout')}
            </Button>
          ) : (
            <Button
              additionalClasses={styles.LoginButton}
              data-cy="cart-button-login"
              onClick={() => dispatch(setOverlayView({ view: { type: 'LOGIN' } }))}
            >
              <ArrowRight />
              {t('login_title')}
            </Button>
          )}
        </div>
      </CartDropdown>
      <m.div
        className={styles.Backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      />
    </>
  )
})

export const CartButton: React.FC<{ className?: string }> = ({ className }) => {
  const locale = useLocale()
  const enabled = useAppSelector((state) => state.config.features.enableCart)
  const addItemNotification = useAppSelector((state) =>
    state.notifications.notifications.find((a) => a.type === 'ADD_PRODUCT'),
  )
  const [isOpen, setIsOpen] = useState(false)
  const dispatch = useAppDispatch()
  const rows = useAppSelector(
    (state) => state.checkout.rows,
    (a, b) => !compareCartRows(a, b),
  )
  const addedRowQuantity = rows.find((row) => addItemNotification?.productId === row.product.id)?.quantity ?? 0
  const contentRef = useRef<HTMLDivElement>(null)
  const parentRef = useRef<HTMLButtonElement>(null)
  const close = (event?: MouseEvent | TouchEvent | KeyboardEvent) => {
    if (
      event?.type === 'mousedown' &&
      (event?.target as HTMLElement)?.attributes?.getNamedItem('data-type')?.value === 'buy'
    ) {
      return
    }
    if (contentRef.current?.contains(event?.target as Node)) {
      return
    }
    dispatch(clearNotification('ADD_PRODUCT'))
    if (!isOpen) {
      return
    }
    event?.preventDefault()
    setIsOpen(false)
  }
  const toggle = (e?: MouseEvent) => {
    if (isOpen) {
      close(e)
    } else {
      setIsOpen(true)
      dispatch(clearNotification('ADD_PRODUCT'))
    }
  }
  useOnClickOutside(parentRef, close)
  // Hide body scrolling if cart is open in mobile
  const from = useBreakpoints().from
  const isDesktopCart = from('tablet')
  const hideBodyScroll = !isDesktopCart && isOpen
  useHideBodyScroll(hideBodyScroll, { restorePosition: true })

  const itemCount = rows.reduce<number>((acc, curr) => acc + curr.quantity, 0)
  const formattedItemCount = new Intl.NumberFormat(supportedLanguages[locale].locale).format(Math.min(itemCount, 99))

  useEffect(() => {
    if (addItemNotification?.skipOpen) {
      return
    }

    if (addItemNotification?.type === 'ADD_PRODUCT') {
      setIsOpen(true)
    }
  }, [addItemNotification])

  const showItemAddedAnimation = !isOpen && addedRowQuantity > 1 && addedRowQuantity < 10
  const features = () => import('../../animations/features').then((a) => a.default)
  if (!enabled) {
    return null
  }

  return (
    <div className={cx(className, styles.CartButton, { [styles.Open]: isOpen })} data-cy="cart-button-container">
      <LazyMotion features={features}>
        <IconButton
          onClick={toggle}
          data-cy="cart-button"
          ref={parentRef}
          className={cx(styles.CartIcon, {
            [styles.Empty]: itemCount === 0,
            [styles.ItemAdded]: showItemAddedAnimation,
          })}
          data-after-content={formattedItemCount}
        >
          <ShoppingBag height={28} width={28} />
        </IconButton>
        <AnimatePresenceIfEnabled enabled={!isDesktopCart}>
          {isOpen && <Cart ref={contentRef} onClose={() => close()} disableAnimations={isDesktopCart} />}
        </AnimatePresenceIfEnabled>
      </LazyMotion>
    </div>
  )
}
